import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  generalInformationFormSchema,
  lungsAdditionalInfoFormSchema,
  patientDetailsFormSchema,
  prostateAdditionalInfoFormSchema,
  referralFormSchema,
} from "../schemas";
import {
  getGeneralInformationDefaultValues,
  getLungsDefaultValues,
  getPatientDetailsDefaultValues,
  getProstateDefaultValues,
  getReferralFormDefaultValues,
} from "../utils/referralDefaults";

export const useReferralForms = ({
  patient,
  patientPreferences,
  patientSocial,
  patientBaseline,
  medicalHistory,
  allergies,
  familyMedicalHistory,
  staffMember,
  consultationsList,
  today,
}) => {
  const activeConsultation = patient?.activeConsultationId
    ? consultationsList?.find(
        (consultation) => consultation.id === patient.activeConsultationId
      )
    : null;

  const generalInformationForm = useForm({
    resolver: zodResolver(generalInformationFormSchema),
    defaultValues: getGeneralInformationDefaultValues(),
  });

  const referralType = generalInformationForm.watch("referralType");
  const isLungsReferral = referralType?.toLowerCase().includes("lung");
  const isProstateReferral = referralType?.toLowerCase().includes("prostate");

  const prostateAdditionalInfoForm = useForm({
    resolver: zodResolver(prostateAdditionalInfoFormSchema),
    defaultValues: getProstateDefaultValues(today),
  });

  const lungsAdditionalInfoForm = useForm({
    resolver: zodResolver(lungsAdditionalInfoFormSchema),
    defaultValues: getLungsDefaultValues(),
  });

  const patientDetailsForm = useForm({
    resolver: zodResolver(patientDetailsFormSchema),
    defaultValues: getPatientDetailsDefaultValues({
      staffMember,
      patient,
      patientPreferences,
    }),
  });

  const referralForm = useForm({
    resolver: async (data, context, options) => {
      return zodResolver(
        referralFormSchema(isLungsReferral, isProstateReferral)
      )(data, context, options);
    },
    defaultValues: getReferralFormDefaultValues({
      activeConsultation,
      medicalHistory,
      allergies,
      familyMedicalHistory,
      patient,
      patientSocial,
      patientBaseline,
      isProstateReferral,
      isLungsReferral,
    }),
  });

  return {
    generalInformationForm,
    patientDetailsForm,
    referralForm,
    prostateAdditionalInfoForm,
    lungsAdditionalInfoForm,
    referralType,
    isLungsReferral,
    isProstateReferral,
  };
};
